'use client'

import { ArrowRight } from 'lucide-react'
import React from 'react'

import {
  LocationCampusDropdown,
  locationMapHeroTriggerClassName,
} from '@/components/Location/LocationCampusDropdown'
import { LocationUniversitySearch } from '@/components/Location/LocationUniversitySearch'
import type { CityId } from '@/lib/mock/mapLocations'
import type { LocationCampusOption, LocationUniversityOption } from '@/lib/mock/locationPages'
import { cn } from '@/utilities/ui'

type LocationMapUniversityFinderCopy = {
  campusesListAriaLabel: string
  clearLocationLabel: string
  clearUniversityLabel: string
  emptyState: string
  selectCampusLabel: string
  selectLocationLabel: string
  selectUniversityLabel: string
  selectUniversityPlaceholder: string
  universitiesListAriaLabel: string
}

type LocationMapUniversityFinderControlsProps = {
  campuses: LocationCampusOption[]
  citySlug: CityId
  exploreLabel: string
  isUniversityFinder: boolean
  mapHeroCopy: LocationMapUniversityFinderCopy
  onCampusClear: () => void
  onCampusSelect: (campus: LocationCampusOption) => void
  onUniversityClear: () => void
  onUniversitySelect: (university: LocationUniversityOption) => void
  routeUnavailableMessage?: string
  selectedCampusSlug: string
  selectedUniversity: LocationUniversityOption | null
  universities: LocationUniversityOption[]
  universityCampusOptions: Array<LocationUniversityOption & LocationCampusOption>
}

export function LocationMapUniversityFinderControls({
  campuses,
  citySlug,
  exploreLabel,
  isUniversityFinder,
  mapHeroCopy,
  onCampusClear,
  onCampusSelect,
  onUniversityClear,
  onUniversitySelect,
  routeUnavailableMessage,
  selectedCampusSlug,
  selectedUniversity,
  universities,
  universityCampusOptions,
}: LocationMapUniversityFinderControlsProps) {
  if (isUniversityFinder) {
    return (
      <div className="pointer-events-auto mt-4 flex w-full min-w-0 flex-col gap-2 md:mt-6">
        <div className="flex flex-wrap items-center gap-2">
          <LocationUniversitySearch
            accentSelected
            clearLabel={mapHeroCopy.clearUniversityLabel}
            emptyLabel={mapHeroCopy.emptyState}
            label={mapHeroCopy.selectUniversityLabel}
            listAriaLabel={mapHeroCopy.universitiesListAriaLabel}
            onClear={onUniversityClear}
            onSelect={onUniversitySelect}
            placeholder={mapHeroCopy.selectUniversityPlaceholder}
            selectedLabelMaxLength={22}
            universities={universities}
            uppercase
            value={selectedUniversity}
            wrapMenuOptions
          />

          <LocationCampusDropdown
            accentSelected
            campuses={campuses}
            citySlug={citySlug}
            clearLabel={mapHeroCopy.clearLocationLabel}
            currentSlug={selectedCampusSlug}
            icon="mapPin"
            label={mapHeroCopy.selectLocationLabel}
            listAriaLabel={mapHeroCopy.campusesListAriaLabel}
            onClear={onCampusClear}
            onSelect={onCampusSelect}
            selectedLabelMaxLength={22}
            uppercase
            variant="map"
          />
        </div>

        {routeUnavailableMessage ? (
          <p className="font-outfit text-sm font-medium text-dark-800/80">
            {routeUnavailableMessage}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="pointer-events-auto mt-6 flex w-full min-w-0 max-w-[22rem] flex-col items-stretch gap-2 md:mt-10">
      <span className={cn(locationMapHeroTriggerClassName, 'self-start')}>{exploreLabel}</span>

      <div className="flex w-full min-w-0 items-center gap-2">
        <LocationUniversitySearch
          accentSelected={false}
          clearLabel={mapHeroCopy.clearUniversityLabel}
          emptyLabel={mapHeroCopy.emptyState}
          fullWidth
          label={mapHeroCopy.selectCampusLabel}
          listAriaLabel={mapHeroCopy.universitiesListAriaLabel}
          onClear={onUniversityClear}
          onSelect={onUniversitySelect}
          placeholder={mapHeroCopy.selectUniversityPlaceholder}
          universities={universityCampusOptions}
          value={selectedUniversity}
        />

        <ArrowRight aria-hidden className="size-4 shrink-0 text-dark-green" />

        <LocationCampusDropdown
          campuses={campuses}
          citySlug={citySlug}
          currentSlug={selectedCampusSlug}
          fullWidth
          icon="mapPin"
          label={mapHeroCopy.selectLocationLabel}
          listAriaLabel={mapHeroCopy.campusesListAriaLabel}
          onSelect={onCampusSelect}
          uppercase
          variant="map"
        />
      </div>
    </div>
  )
}
